import React, { useState } from 'react';
import { Building, Users, Calendar, Plus, Star, ArrowRight, Folder, Briefcase, Home, Heart, Globe, Zap, Target, ChevronDown, MoreVertical, Trash2, Edit } from 'lucide-react';
import WorkspaceMembers from './WorkspaceMembers';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuLabel,
  DropdownMenuSeparator,
} from './ui/dropdown-menu';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from './ui/alert-dialog';

const WorkspaceIndex = ({ 
  workspaces, 
  allBoards, 
  currentUser,
  onWorkspaceSelect, 
  onCreateWorkspace, 
  onUpdateWorkspace, 
  onDeleteWorkspace 
}) => {
  const [hoveredWorkspace, setHoveredWorkspace] = useState(null);
  const [deleteWorkspace, setDeleteWorkspace] = useState(null);
  
  // Workspace icon options
  const workspaceIcons = [
    { name: 'Building', component: Building, description: 'Office/Company' },
    { name: 'Briefcase', component: Briefcase, description: 'Business/Professional' },
    { name: 'Home', component: Home, description: 'Personal/Family' },
    { name: 'Star', component: Star, description: 'Important/Featured' },
    { name: 'Heart', component: Heart, description: 'Passion Projects' },
    { name: 'Folder', component: Folder, description: 'Documentation' },
    { name: 'Users', component: Users, description: 'Team/Collaboration' },
    { name: 'Globe', component: Globe, description: 'Global/International' },
    { name: 'Zap', component: Zap, description: 'Innovation/Startup' },
    { name: 'Target', component: Target, description: 'Goals/Objectives' }
  ];

  // Get icon component by name (default to Building)
  const getWorkspaceIcon = (iconName) => {
    const iconData = workspaceIcons.find(icon => icon.name === iconName);
    return iconData ? iconData.component : Building;
  };

  // Handle icon change
  const handleIconChange = async (workspace, newIconName) => {
    try {
      await onUpdateWorkspace(workspace.id, { ...workspace, icon: newIconName });
    } catch (error) {
      console.error('Failed to update workspace icon:', error);
    }
  };

  const getWorkspaceStats = (workspaceId) => {
    const workspaceBoards = allBoards?.filter(board => board.workspace_id === workspaceId) || [];
    const totalLists = workspaceBoards.reduce((acc, board) => acc + (board.lists?.length || 0), 0);
    const totalCards = workspaceBoards.reduce((acc, board) => {
      return acc + (board.lists?.reduce((listAcc, list) => listAcc + (list.cards?.length || 0), 0) || 0);
    }, 0);
    const starredBoards = workspaceBoards.filter(board => board.starred).length;

    return {
      boardCount: workspaceBoards.length,
      listCount: totalLists,
      cardCount: totalCards,
      starredCount: starredBoards,
      boards: workspaceBoards
    };
  };

  const getRecentActivity = (workspace) => {
    const stats = getWorkspaceStats(workspace.id);
    if (stats.boardCount === 0) return 'No boards yet';
    if (stats.cardCount === 0) return 'No tasks yet';
    return `${stats.cardCount} task${stats.cardCount !== 1 ? 's' : ''} across ${stats.boardCount} board${stats.boardCount !== 1 ? 's' : ''}`;
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100">
      {/* Workspaces Grid */}
      <div className="max-w-7xl mx-auto px-6 py-8">
        {workspaces && workspaces.length > 0 ? (
          <>
            <div className="mb-6 flex items-center justify-between">
              <div>
                <h2 className="text-2xl font-semibold text-gray-800 mb-2">
                  Your Workspaces
                </h2>
                <p className="text-gray-600">
                  Select a workspace to view and manage its boards
                </p>
              </div>
              
              <Button
                onClick={() => {
                  // Create a new workspace with default values
                  const workspaceName = prompt('Enter workspace name:');
                  if (workspaceName && workspaceName.trim()) {
                    onCreateWorkspace({
                      name: workspaceName.trim(),
                      description: '',
                      icon: 'Building'
                    });
                  }
                }}
                className="bg-[#4FC3F7] hover:bg-[#29B6F6] text-white rounded-full p-3 shadow-lg transition-all hover:scale-105"
                title="Create new workspace"
              >
                <Plus className="h-5 w-5" />
              </Button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
              {workspaces.map((workspace) => {
                const stats = getWorkspaceStats(workspace.id);
                const isHovered = hoveredWorkspace === workspace.id;

                return (
                  <Card
                    key={workspace.id}
                    className={`group cursor-pointer transition-all duration-300 hover:shadow-lg hover:-translate-y-1 border-0 ${
                      isHovered ? 'shadow-xl' : 'shadow-md'
                    }`}
                    onMouseEnter={() => setHoveredWorkspace(workspace.id)}
                    onMouseLeave={() => setHoveredWorkspace(null)}
                    onClick={() => onWorkspaceSelect(workspace)}
                  >
                    <CardHeader className="pb-3">
                      <div className="flex items-start justify-between">
                        <div className="flex items-center space-x-3">
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button 
                                variant="ghost" 
                                size="sm"
                                className="p-2 bg-cyan-50 hover:bg-cyan-100 rounded-lg transition-colors group"
                                onClick={(e) => e.stopPropagation()}
                              >
                                {(() => {
                                  const IconComponent = getWorkspaceIcon(workspace.icon);
                                  return <IconComponent className="h-6 w-6 text-cyan-500 group-hover:scale-110 transition-transform" />;
                                })()}
                                <ChevronDown className="h-3 w-3 text-blue-400 absolute -bottom-1 -right-1 opacity-0 group-hover:opacity-100 transition-opacity" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="center" className="w-64">
                              <DropdownMenuLabel>Choose Workspace Icon</DropdownMenuLabel>
                              <DropdownMenuSeparator />
                              <div className="grid grid-cols-5 gap-2 p-2">
                                {workspaceIcons.map((icon) => {
                                  const IconComponent = icon.component;
                                  const isSelected = workspace.icon === icon.name;
                                  return (
                                    <DropdownMenuItem
                                      key={icon.name}
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handleIconChange(workspace, icon.name);
                                      }}
                                      className={`flex items-center justify-center p-3 rounded-lg cursor-pointer ${
                                        isSelected 
                                          ? 'bg-cyan-100 border-2 border-cyan-400' 
                                          : 'hover:bg-gray-100'
                                      }`}
                                      title={`${icon.name} - ${icon.description}`}
                                    >
                                      <IconComponent className={`h-5 w-5 ${
                                        isSelected ? 'text-cyan-500' : 'text-gray-600'
                                      }`} />
                                    </DropdownMenuItem>
                                  );
                                })}
                              </div>
                            </DropdownMenuContent>
                          </DropdownMenu>
                          <div className="flex-1">
                            <div className="flex items-center justify-between">
                              <CardTitle className="text-lg font-semibold text-gray-900 truncate">
                                {workspace.name}
                              </CardTitle>
                              
                              {/* Workspace Options Dropdown */}
                              <DropdownMenu>
                                <DropdownMenuTrigger asChild>
                                  <Button 
                                    variant="ghost" 
                                    size="sm" 
                                    className="h-8 w-8 p-0 opacity-0 group-hover:opacity-100 transition-opacity"
                                    onClick={(e) => e.stopPropagation()}
                                  >
                                    <MoreVertical className="h-4 w-4" />
                                  </Button>
                                </DropdownMenuTrigger>
                                <DropdownMenuContent align="end" onClick={(e) => e.stopPropagation()}>
                                  <DropdownMenuItem 
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      // Add edit functionality here later if needed
                                      console.log('Edit workspace:', workspace.name);
                                    }}
                                  >
                                    <Edit className="h-4 w-4 mr-2" />
                                    Edit workspace
                                  </DropdownMenuItem>
                                  <DropdownMenuSeparator />
                                  <DropdownMenuItem 
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      setDeleteWorkspace(workspace);
                                    }}
                                    className="text-red-600 focus:text-red-600"
                                  >
                                    <Trash2 className="h-4 w-4 mr-2" />
                                    Delete workspace
                                  </DropdownMenuItem>
                                </DropdownMenuContent>
                              </DropdownMenu>
                            </div>
                            
                            {workspace.description && (
                              <p className="text-sm text-gray-500 mt-1 line-clamp-2">
                                {workspace.description}
                              </p>
                            )}
                          </div>
                        </div>
                        <ArrowRight className={`h-5 w-5 text-gray-400 transition-all duration-300 ${
                          isHovered ? 'text-cyan-500 translate-x-1' : ''
                        }`} />
                      </div>
                    </CardHeader>

                    <CardContent className="pt-0">
                      {/* Stats */}
                      <div className="grid grid-cols-2 gap-4 mb-4">
                        <div className="text-center">
                          <div className="text-2xl font-bold text-cyan-500">
                            {stats.boardCount}
                          </div>
                          <div className="text-xs text-gray-500">
                            Board{stats.boardCount !== 1 ? 's' : ''}
                          </div>
                        </div>
                        <div className="text-center">
                          <div className="text-2xl font-bold text-green-600">
                            {stats.cardCount}
                          </div>
                          <div className="text-xs text-gray-500">
                            Task{stats.cardCount !== 1 ? 's' : ''}
                          </div>
                        </div>
                      </div>

                      {/* Activity */}
                      <div className="text-xs text-gray-600 mb-3">
                        {getRecentActivity(workspace)}
                      </div>

                      {/* Badges */}
                      <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-2">
                          {stats.starredCount > 0 && (
                            <Badge variant="secondary" className="text-xs">
                              <Star className="h-3 w-3 mr-1 text-yellow-500" />
                              {stats.starredCount} starred
                            </Badge>
                          )}
                        </div>
                        
                        {stats.boards.length > 0 && (
                          <div className="flex -space-x-1">
                            {stats.boards.slice(0, 3).map((board, index) => (
                              <div
                                key={board.id}
                                className="w-4 h-4 rounded border-2 border-white"
                                style={{ backgroundColor: board.color }}
                                title={board.title}
                              />
                            ))}
                            {stats.boards.length > 3 && (
                              <div className="w-4 h-4 rounded border-2 border-white bg-gray-300 flex items-center justify-center">
                                <span className="text-xs text-gray-600 font-medium">
                                  +{stats.boards.length - 3}
                                </span>
                              </div>
                            )}
                          </div>
                        )}
                      </div>

                      {/* Members Management */}
                      <div className="mt-4 pt-3 border-t border-gray-100">
                        <div onClick={(e) => e.stopPropagation()}>
                          <WorkspaceMembers 
                            workspace={workspace}
                            currentUser={currentUser}
                            onWorkspaceUpdate={onUpdateWorkspace}
                          />
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          </>
        ) : (
          <div className="text-center py-16">
            <div className="max-w-md mx-auto">
              <div className="p-6 bg-white rounded-lg shadow-md">
                <Building className="h-16 w-16 text-gray-300 mx-auto mb-4" />
                <h3 className="text-xl font-semibold text-gray-700 mb-2">
                  No Workspaces Yet
                </h3>
                <p className="text-gray-500 mb-6">
                  Create your first workspace to start organizing your projects and boards.
                </p>
                
                <Button
                  onClick={() => {
                    // Create a new workspace with default values
                    const workspaceName = prompt('Enter workspace name:');
                    if (workspaceName && workspaceName.trim()) {
                      onCreateWorkspace({
                        name: workspaceName.trim(),
                        description: '',
                        icon: 'Building'
                      });
                    }
                  }}
                  className="bg-[#4FC3F7] hover:bg-[#29B6F6] text-white rounded-full p-3 shadow-lg transition-all hover:scale-105 mx-auto"
                  title="Create new workspace"
                >
                  <Plus className="h-5 w-5" />
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>
      
      {/* Delete Workspace Confirmation Dialog */}
      <AlertDialog open={!!deleteWorkspace} onOpenChange={() => setDeleteWorkspace(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Workspace</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete "{deleteWorkspace?.name}"? This action cannot be undone and will permanently delete the workspace and all its boards.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={async () => {
                if (deleteWorkspace) {
                  try {
                    await onDeleteWorkspace(deleteWorkspace.id);
                    setDeleteWorkspace(null);
                  } catch (error) {
                    console.error('Failed to delete workspace:', error);
                    // Keep dialog open if deletion fails
                  }
                }
              }}
              className="bg-red-600 hover:bg-red-700 focus:ring-red-600"
            >
              Delete Workspace
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default WorkspaceIndex;